import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode"; // Decode JWT to get user ID
import { canUsePilotFeatures } from "../featureAccess";
import "./baseStyles.css";
import "./CampaignCatalogPage.css";

const apiUrl = process.env.REACT_APP_API_URL;

const CampaignCatalogPage = () => {
  const navigate = useNavigate();

  const [campaignCatalog, setCampaignCatalog] = useState([]);
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [cognitoUserId, setCognitoUserId] = useState(null);
  const [loginUserId, setLoginUserId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");

  const [showCampaignNameModal, setShowCampaignNameModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showTemplateSelectorModal, setShowTemplateSelectorModal] =
    useState(false);

  const [customCampaignName, setCustomCampaignName] = useState(""); // Personal Reminders label
  const [partnerLoginId, setPartnerLoginId] = useState(""); // Favorites partner login
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isCommercialReminder, setIsCommercialReminder] = useState(false);

  const [longDescriptionHTML, setLongDescriptionHTML] = useState(null);
  const [activeTab, setActiveTab] = useState("Tools");

  // Big Question fields
  const [groupName, setGroupName] = useState("");
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [questionSource, setQuestionSource] = useState("none"); // 'none' | 'template'
  const [selectedRadioIndex, setSelectedRadioIndex] = useState(null);

  // Big Question templates (non-custom)
  const nonCustomBigQuestionTemplates = campaignCatalog.filter(
    (c) =>
      c.campaignCatalogType === "BigQuestion" &&
      !c.campaignCatalogRealName.includes("_listOfQuestions")
  );

  // --- Auth bootstrap ---
  useEffect(() => {
    const idToken = localStorage.getItem("idToken");

    if (!idToken) {
      console.warn("🚨 No idToken found! Redirecting to login...");
      setError("User is not authenticated. Please log in.");
      navigate("/login");
      return;
    }

    try {
      const decodedToken = jwtDecode(idToken);

      if (decodedToken.exp * 1000 < Date.now()) {
        console.warn("🚨 Token expired. Redirecting to login...");
        localStorage.removeItem("idToken");
        setError("Session expired. Please log in again.");
        navigate("/login");
        return;
      }

      const loginId = decodedToken["cognito:username"];

      setLoginUserId(loginId);
      setCognitoUserId(decodedToken.sub);
      fetchCampaignCatalog(decodedToken.sub);
      fetchUserCampaigns(decodedToken.sub);
    } catch (err) {
      console.error("❌ Error decoding token:", err);
      setError("Authentication error. Please log in again.");
      localStorage.removeItem("idToken");
      navigate("/login");
    }
  }, [navigate]);

  // --- Helpers / API wrappers ---

  const handleAuthError = (err) => {
    console.error("❌ API Request Failed:", err);

    if (err.response && err.response.status === 401) {
      console.warn("🚨 Unauthorized! Redirecting to login...");
      localStorage.removeItem("idToken");
      setError("Session expired. Please log in again.");
      navigate("/login");
    } else {
      setError("An error occurred. Please try again.");
    }
  };

  const fetchCampaignCatalog = async (userid) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}`, {
        params: { operation: "fetch_all_catalog", userid },
        headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
      });
      setCampaignCatalog(response.data || []);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCampaigns = async (userid) => {
    try {
      const response = await axios.get(`${apiUrl}`, {
        params: { operation: "fetch_all", userid },
        headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
      });
      setUserCampaigns(response.data || []);
    } catch (err) {
      handleAuthError(err);
    }
  };

  const isCampaignSelected = (realName) => {
    return userCampaigns.some((campaign) => {
      const userRealName = campaign.campaignListRealCampaignName || "";
      const userType = campaign.campaignListCampaignType;

      if (!userRealName || !userType) return false;

      // exact match for most
      if (userRealName === realName) return true;

      // special case for BigQuestion
      const isCatalogBigQuestion = realName.toLowerCase().includes("bigquestion");
      const isUserBigQuestion = userType === "BigQuestion";

      if (isCatalogBigQuestion && isUserBigQuestion) return true;

      return false;
    });
  };

  // --- Campaign selection handler ---

  const handleCampaignSelect = async (campaign) => {
    if (!cognitoUserId) {
      setError("User not authenticated.");
      return false;
    }

    if (campaign.campaignCatalogType === "PersonalReminders") {
      setIsCommercialReminder(false);
      setShowCampaignNameModal(true);
      setSelectedCampaign(campaign);
      return true;
    }

    if (campaign.campaignCatalogType === "Favorites") {
      setShowPartnerModal(true);
      setSelectedCampaign(campaign);
      return true;
    }

    if (campaign.campaignCatalogType === "BigQuestion") {
      setSelectedCampaign(campaign);
      setQuestionSource("none");
      setGroupName("");
      setSelectedTemplateName("");
      setModalError("");
      setShowTemplateSelectorModal(true);
      return true;
    }

    if (campaign.campaignCatalogType === "CommercialReminders") {
      setIsCommercialReminder(true);
      setSelectedCampaign(campaign);
      setCustomCampaignName(campaign.campaignCatalogName);
      setShowCampaignNameModal(true);
      return true;
    }

    if (campaign.campaignCatalogType === "SystemTool") {
      if ((campaign.campaignCatalogName || "").toLowerCase() === "organizer") {
        await startMyOrganizer(campaign);
        return true;
      }

      await addCampaignToUserList(
        campaign.campaignCatalogName,
        campaign.campaignCatalogRealName,
        campaign.campaignCatalogType
      );
      await fetchUserCampaigns(cognitoUserId);
      navigate("/list-manager");
      return true;
    }

    await addCampaignToUserList(
      campaign.campaignCatalogName,
      campaign.campaignCatalogRealName,
      campaign.campaignCatalogType
    );
    navigate(`/item-manager/${campaign.campaignCatalogName}`);
    return true;
  };

  // --- Utilities for Big Question naming ---

  const dedupeName = (name) => {
    const existing = userCampaigns.map(
      (c) => (c.campaignName || "").toLowerCase()
    );
    if (!existing.includes(name.toLowerCase())) return name;
    let i = 2;
    while (existing.includes(`${name}-${i}`.toLowerCase())) i++;
    return `${name}-${i}`;
  };

  const slugifyName = (s) =>
    (s || "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^A-Za-z0-9_-]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");

  const handleBigQuestionSubmit = async (opts = {}) => {
    const src = (opts.templateSource ?? questionSource).trim(); // 'template' | 'none'
    const grp = (opts.groupName ?? groupName ?? "").trim();
    const selTemplate = (opts.templateName ?? selectedTemplateName ?? "").trim();

    if (!grp) {
      setError("Please enter a group name.");
      setModalError("Please enter a group name.");
      return;
    }
    if (src === "template" && !selTemplate) {
      setError("Please choose a template.");
      setModalError("Please choose a template.");
      return;
    }

    const groupSlug = slugifyName(grp);
    const baseName = `BigQuestion_${groupSlug}`;
    const campaignType = "BigQuestion";

    const displayName = dedupeName(baseName);
    const campaignName = `${cognitoUserId}_${displayName}`;
    const realCampaignName = campaignName;

    let templateName;
    let templateBased;

    if (src === "template") {
      const tBase = selTemplate.split("_").slice(1).join("_") || selTemplate;
      templateName = `${tBase}Template`;
      templateBased = true;
    } else {
      templateName = campaignName;
      templateBased = false;
    }

    const payload = {
      operation: "start_big_question_campaign_custom",
      userid: cognitoUserId,
      loginUserId,
      campaignName,
      campaignListRealCampaignName: realCampaignName,
      campaignListCampaignType: campaignType,
      campaignListCommMethod: "email",
      campaignListNextSeq: 1,
      campaignListSendWhen: "daily",
      campaignListTemplateName: templateName,
      campaignListTemplateBased: templateBased,
    };

    try {
      await axios.post(apiUrl, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
      });
      setShowTemplateSelectorModal(false);
      navigate(`/big-question/${realCampaignName}`);
    } catch (err) {
      console.error("❌ Error starting Big Question campaign:", err);
      setError("Failed to start Big Question campaign. Please try again.");
    }
  };

  // --- myOrganizer setup flow ---

  const startMyOrganizer = async (campaign) => {
    if (!cognitoUserId) {
      setError("User not authenticated.");
      return;
    }

    setLoading(true);
    setError("");

    const realName = (campaign.campaignCatalogRealName || "{userid}_myOrganizer").replace(
      "{userid}",
      cognitoUserId
    );

    try {
      const response = await axios.post(
        apiUrl,
        {
          operation: "start_myorganizer",
          userid: cognitoUserId,
          loginUserId,
          campaignName: campaign.campaignCatalogName || "Organizer",
          campaignListRealCampaignName: realName,
          campaignListCampaignType: "SystemTool",
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
        }
      );

      await fetchUserCampaigns(cognitoUserId);




      let quickCaptureUrl = response.data?.quickCaptureUrl;

      if (quickCaptureUrl && window.location.hostname === "localhost") {
        const quickPath = new URL(quickCaptureUrl).pathname;
        quickCaptureUrl = `${window.location.origin}${quickPath}`;
      }

      navigate("/myorganizer-setup", {
        state: {
          quickCaptureUrl,
          needsTokenRotationForLink: response.data?.needsTokenRotationForLink,
        },
      });





      return;


    } catch (err) {
      console.error("Error starting myOrganizer:", err);
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };

  // --- Add campaign to user list ---

  const addCampaignToUserList = async (campaignName, realName, campaignType) => {
    const payload = {
      operation: "add_list_item",
      userid: cognitoUserId,
      campaignName,
      campaignListRealCampaignName: realName,
      campaignListCampaignType: campaignType,
      campaignListCommMethod: "email",
      campaignListSendWhen: "readNext",
      campaignListNextSeq: 1,
      campaignListReminderSchedule: "daily",
      campaignListReminderTime: "08:00",
      campaignListReminderDays: "",
    };

    try {
      await axios.post(`${apiUrl}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
      });
      fetchUserCampaigns(cognitoUserId);
    } catch (err) {
      handleAuthError(err);
    }
  };

  // --- Favorites flow ---

  const fetchCustomerData = async (loginIdRaw) => {
    setLoading(true);
    setError("");

    const safePartnerLoginId = (loginIdRaw || "").trim();

    try {
      const response = await axios.get(`${apiUrl}`, {
        params: { operation: "fetch_customer_by_login", userid: safePartnerLoginId },
        headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
      });

      return response.data;
    } catch (err) {
      console.error("Error fetching customer data:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleAddPartner = async (campaignName) => {
    const safePartnerLoginId = (partnerLoginId || "").trim();

    if (!safePartnerLoginId) {
      setError("Please enter a partner's login ID.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(
        `${apiUrl}`,
        {
          operation: "update_campaign_partners",
          userid: cognitoUserId,
          campaignName,
          partnerLoginId: safePartnerLoginId,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
        }
      );

      fetchUserCampaigns(cognitoUserId);
    } catch (err) {
      console.error("Error adding partner:", err);
      setError("Failed to add partner. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartFavoritesCampaign = async () => {
    const safePartnerLoginId = (partnerLoginId || "").trim();

    if (!cognitoUserId || !safePartnerLoginId) {
      setError("User and partner must be provided.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Step 0 – confirm partner exists
      const partnerData = await fetchCustomerData(safePartnerLoginId);

      if (!partnerData) {
        setError("The partner has not yet enrolled at myDailyReminders.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      const realName = `${cognitoUserId}_Favorites`;

      // Step 1 – start Favorites campaign
      await axios.post(
        `${apiUrl}`,
        {
          operation: "start_favorites_campaign",
          userid: cognitoUserId,
          loginUserId,
          campaignName: realName,
          campaignListCommMethod: "email",
          campaignListNextSeq: 1,
          campaignListSendWhen: "daily",
          campaignListRealCampaignName: realName,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
        }
      );

      // Step 2 – add partner
      await handleAddPartner(realName);

      fetchUserCampaigns(cognitoUserId);
      navigate(`/myfavorites?campaignName=${encodeURIComponent(realName)}`);
    } catch (err) {
      console.error("Error starting favorites campaign:", err);
      setError("Failed to start Favorites campaign. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // --- Reminder campaign modal submit ---

  const handleReminderCampaignSubmit = async () => {
    if (!customCampaignName.trim()) {
      setError("Campaign name cannot be empty.");
      return;
    }

    let realName;
    let campaignName;
    let campaignType;

    if (isCommercialReminder) {
      realName = selectedCampaign.campaignCatalogRealName;
      campaignName = customCampaignName.trim();
      campaignType = "CommercialReminders";
    } else {
      realName = `${cognitoUserId}_${customCampaignName.trim()}`;
      campaignName = customCampaignName.trim();
      campaignType = "PersonalReminders";
    }

    await addCampaignToUserList(campaignName, realName, campaignType);

    setShowCampaignNameModal(false);
    setCustomCampaignName("");

    navigate(`/item-manager/${campaignName}`);
  };

  // --- Navigation ---

  const goToCampaign = (campaign) => {
    let dest = "";

    const name = campaign.campaignName || campaign.campaignCatalogName;
    const type =
      campaign.campaignListCampaignType || campaign.campaignCatalogType;

    if (type === "Favorites")
      dest = `/myfavorites?campaignName=${encodeURIComponent(name)}`;
    else if (["PersonalReminders", "CommercialReminders"].includes(type))
      dest = `/item-manager/${encodeURIComponent(name)}`;
    else if (type === "BigQuestion")
      dest = `/big-question/${name}`;
    else if (type === "SystemTool" || type === "myOrganizer")
      dest = `/item-manager/${encodeURIComponent(name)}`;
    else
      dest = `/item-manager/${encodeURIComponent(name)}`;

    navigate(dest);
  };



  // --- More Info button renderer ---

  const renderMoreInfoButton = (campaign) => {
    return (
      <button
        className="info-button"
        onClick={async () => {
          const campaignType =
            campaign.campaignListCampaignType || campaign.campaignCatalogType;
          const realName =
            campaign.campaignCatalogRealName ||
            campaign.campaignListRealCampaignName ||
            "";

          let htmlContent = "";

          if (campaignType === "CommercialReminders") {
            htmlContent = `
              <div style="text-align: center;">
                <p class="modal-description">
                  <i>Commercial reminders are curated lists based on books, self-improvement programs, or business resources. This is a summary of those works:</i>
                </p>
                <br/>
            `;

            try {
              const slugCandidates = [
                realName,
                realName.replace(/\s+/g, ""),
                realName.toLowerCase().replace(/\s+/g, ""),
              ];

              let customHtml = null;

              for (const slug of slugCandidates) {
                const url = new URL(
                  `/campaign-details/${slug}.html`,
                  window.location.origin
                ).href;
                const res = await fetch(url, { cache: "no-store" });
                const text = await res.text();

                const looksLikeSpaShell =
                  /<div id=["']root["']><\/div>/.test(text) ||
                  /<title>myDailyReminders<\/title>/.test(text);

                if (res.ok && !looksLikeSpaShell) {
                  customHtml = text;
                  break;
                }
              }

              htmlContent += `
                <div class="custom-campaign-details">
                  ${customHtml ?? "<p>No additional details available.</p>"}
                </div>
              </div>`;
            } catch (err) {
              console.error(
                "Error loading specific Commercial Reminder details",
                err
              );
              htmlContent += `<div class="custom-campaign-details"><p>No additional details available.</p></div></div>`;
            }
          } else if (campaignType === "Favorites") {
            htmlContent = `
              <div style="text-align: center;">
                <h2 class="modal-title">Favorites Campaign</h2>
                <p class="modal-description">
                  "My Favorites" is a playful and powerful connection tool. You and 
                  your partner both answer personal questions like “What’s your favorite food?” 
                  or “What song makes you cry?” — then guess each other’s responses. It's 
                  revealing, fun, and often hilarious. When both partners finish, you’ll see how 
                  close your answers were and get a snapshot of how well you really know each 
                  other. It’s like a relationship quiz, with heart.
                </p>
              </div>
            `;
          } else if (campaignType === "BigQuestion") {
            htmlContent = `
              <div style="text-align: center;">
                <h2 class="modal-title">The Big Question</h2>
                <p class="modal-description">
                  In "The Big Question," each participant answers a deep, curious, or 
                  funny question. Once everyone has submitted, our AI reads the 
                  responses and chooses the best, the wittiest, or the most meaningful one — then 
                  sends out a group review. It’s perfect for friends, families, or teams who want 
                  to stay connected, reflect together, and have a few laughs along the way.
                </p>
                <p>
                  <a 
                    href="/campaign-details/BigQuestionSamples.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style="font-size: 0.9rem; color: #007bff; text-decoration: underline;"
                  >
                    👉 See Sample Questions
                  </a>
                </p>
              </div>
            `;
          } else if (campaignType === "PersonalReminders") {
            htmlContent = `
              <div style="text-align: center;">
                <h2 class="modal-title">Personal Reminders</h2>
                <p class="modal-description">
                  Personal Reminders are self-curated lists of thoughts, quotes, habits, or 
                  concepts you want to reinforce. Whether you’re learning something new, trying 
                  to build a habit, or just want to laugh — your daily messages will help it 
                  stick. Think of them as micro affirmations, brain snacks, or gentle nudges to 
                  become the person you're trying to be. Customize everything — it’s your growth 
                  journey.
                </p>
              </div>
            `;
          } else {
            htmlContent = `
              <div style="text-align: center;">
                <h2 class="modal-title">Campaign Details</h2>
                <p class="modal-description">
                  More information about this campaign will be available soon. Stay tuned!
                </p>
              </div>
            `;
          }

          setLongDescriptionHTML(htmlContent);
        }}
        title="More Info"
      >
        More Info
      </button>
    );
  };

  // --- JSX ---

  return (
    <div className="container campaign-catalog-page">
      <h2 className="header desktop-only">Available Products</h2>
      <h2 className="header mobile-only">Available Products</h2>

      <br />

      {/* Tabs */}
      <div className="tab-buttons">
        {["All", "Tools"].map((tab) => (
          <button
            key={tab}
            className={`tab-button ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
        <button
          className={`tab-button ${
            activeTab === "CommercialReminders" ? "active" : ""
          }`}
          onClick={() => setActiveTab("CommercialReminders")}
        >
          Commercial Reminders
        </button>
      </div>

      {error && <p className="error-message">{error}</p>}
      {loading && <p className="loading-text">Loading...</p>}

      {/* Campaign cards */}
      <div className="catalog-list">
        <br />
        {campaignCatalog
          .filter((campaign) => {
            // Pilot-only products: hide Organizer unless this is a pilot user.
            if (
              campaign.campaignCatalogType === "SystemTool" &&
              (campaign.campaignCatalogName || "").toLowerCase() === "organizer" &&
              !canUsePilotFeatures(cognitoUserId)
            ) {
              return false;
            }

            if (
              campaign.campaignCatalogType === "BigQuestion" &&
              campaign.campaignCatalogName !== "the Big Question"
            ) {
              return false;
            }
            if (activeTab === "All") return true;
            if (activeTab === "Tools") {
              return campaign.campaignCatalogType !== "CommercialReminders";
            }
            return campaign.campaignCatalogType === activeTab;
          })
          .map((campaign, index) => {
            const isSelected = isCampaignSelected(
              campaign.campaignCatalogRealName
            );
            const isBigQuestion = campaign.campaignCatalogType === "BigQuestion";
            const shouldDisable = isSelected && !isBigQuestion;
            const isRadioChecked =
              selectedRadioIndex === index || (shouldDisable && isSelected);

            return (
              <div key={index} className="campaign-card">
                <div className="card-header">
                  <div className="radio-wrapper">
                    <input
                      type="radio"
                      checked={isRadioChecked}
                      disabled={shouldDisable}
                      onChange={async () => {
                        setSelectedRadioIndex(index);
                        const confirmed = await handleCampaignSelect(campaign);
                        if (!confirmed) setSelectedRadioIndex(null);
                      }}
                    />
                  </div>

                  <div className="campaign-name">
                    <span
                      className={`campaign-name-label ${
                        isSelected ? "selected" : ""
                      }`}
                      onClick={() =>
                        isSelected && goToCampaign(campaign, cognitoUserId)
                      }
                      title={isSelected ? "Go to Campaign" : ""}
                    >
                      {campaign.campaignCatalogName}
                      {isSelected && (
                        <span className="check" title="Already Joined">
                          ✅
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {campaign.campaignCatalogComment && (
                  <div className="campaign-comment">
                    {campaign.campaignCatalogComment}
                  </div>
                )}

                <div className="campaign-description">
                  {campaign.campaignCatalogShortDescription ||
                    campaign.campaignCatalogShortDesc}
                </div>

                <br />

                <div className="campaign-footer">
                  {renderMoreInfoButton(campaign)}
                  <div className="campaign-cost">
                    {campaign.campaignCatalogCost === "0"
                      ? "Free"
                      : `$${campaign.campaignCatalogCost}`}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Personal / Commercial Reminders Modal */}
      {showCampaignNameModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="edit-options-container">
              <h3>Enter Campaign Name</h3>
              <input
                type="text"
                value={customCampaignName}
                onChange={(e) => setCustomCampaignName(e.target.value)}
                placeholder="Enter campaign name"
              />
              <div className="button-group">
                <button
                  className="save-button"
                  onClick={handleReminderCampaignSubmit}
                >
                  Create Campaign
                </button>
                <button
                  className="save-button"
                  onClick={() => setShowCampaignNameModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partner Entry Modal (Favorites) */}
      {showPartnerModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="edit-options-container">
              <h3>Enter Partner's Login ID</h3>
              <input
                type="text"
                placeholder="Partner Login ID"
                value={partnerLoginId}
                onChange={(e) => setPartnerLoginId(e.target.value)}
              />
              <div className="button-group">
                <button
                  className="save-button"
                  onClick={handleStartFavoritesCampaign}
                >
                  Start Favorites Campaign
                </button>
                <button
                  className="save-button"
                  onClick={() => setShowPartnerModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Long description modal */}
      {longDescriptionHTML && (
        <div
          className="modal-overlay"
          onClick={() => setLongDescriptionHTML(null)}
        >
          <div
            className="modal large-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content-bright"
              style={{
                padding: "24px",
                borderRadius: "12px",
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              <div style={{ maxWidth: "500px", margin: "0 auto" }}>
                <div
                  dangerouslySetInnerHTML={{ __html: longDescriptionHTML }}
                />
                <div
                  className="button-container"
                  style={{ marginTop: "24px", textAlign: "center" }}
                >
                  <button
                    className="save-button"
                    onClick={() => setLongDescriptionHTML(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Big Question Template Selector Modal */}
      {showTemplateSelectorModal && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bq-title"
        >
          <div className="modal-content">
            <div className="edit-options-container stack-md" style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3 id="bq-title" className="modal-header">
                  Start a Big Question Campaign
                </h3>
                <a
                  href="https://mydailyreminders.com/big-question-help"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="help-link"
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: "400",
                    color: "#2563eb",
                    textDecoration: "underline",
                  }}
                >
                  (?)
                </a>
              </div>

              <br />

              {/* Group name */}
              <label className="field-label">Enter your Group name:</label>
              <input
                type="text"
                className="field-input"
                placeholder="e.g., Family, Team A"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />

              <br />

              {/* Question source */}
              <div
                className="stack-sm source-radio-group"
                style={{ marginTop: 6 }}
              >
                <div className="field-label" style={{ marginBottom: 6 }}>
                  Now we just need to know where the questions will come from:
                </div>

                <label className="radio-row">
                  <input
                    type="radio"
                    value="none"
                    checked={questionSource === "none"}
                    onChange={() => setQuestionSource("none")}
                  />
                  <span>Start empty (you&apos;ll add questions later)</span>
                </label>

                <label className="radio-row">
                  <input
                    type="radio"
                    value="template"
                    checked={questionSource === "template"}
                    onChange={() => setQuestionSource("template")}
                  />
                  <span>Use our questions</span>
                </label>
              </div>

              {/* Template choices */}
              {questionSource === "template" && (
                <section
                  aria-labelledby="template-title"
                  className="stack-sm"
                  style={{ marginTop: 10 }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <h3
                      id="template-title"
                      className="modal-header"
                      style={{ margin: 6 }}
                    >
                      Template
                    </h3>
                    <a
                      href="https://mydailyreminders.com/big-question-help"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="help-link"
                      style={{
                        fontSize: "0.9rem",
                        fontWeight: 400,
                        color: "#2563eb",
                        textDecoration: "underline",
                      }}
                    >
                      Help on questions
                    </a>
                  </div>

                  <div
                    id="template-choice"
                    className="template-radio-group"
                    role="radiogroup"
                    aria-label="Template choices"
                  >
                    {nonCustomBigQuestionTemplates.map((t) => (
                      <label
                        key={t.campaignCatalogRealName}
                        className="radio-row template-radio"
                      >
                        <input
                          type="radio"
                          name="templateChoice"
                          value={t.campaignCatalogRealName}
                          checked={
                            selectedTemplateName === t.campaignCatalogRealName
                          }
                          onChange={() =>
                            setSelectedTemplateName(t.campaignCatalogRealName)
                          }
                        />
                        <span>{t.campaignCatalogName}</span>
                      </label>
                    ))}
                  </div>
                </section>
              )}

              {modalError && (
                <div className="error-inline">{modalError}</div>
              )}

              <br />
              <div className="button-group" style={{ marginTop: 4 }}>
                <button
                  className="save-button"
                  onClick={() =>
                    handleBigQuestionSubmit({
                      groupName: groupName.trim(),
                      templateSource: questionSource,
                      templateName: selectedTemplateName,
                    })
                  }
                >
                  Start Campaign
                </button>
                <button
                  className="save-button"
                  onClick={() => setShowTemplateSelectorModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignCatalogPage;
